import fetchApi from '../util/fetchApi';
import checkErrors from './checkErrors';
import checkDocumentResponse, { STATUS } from './checkDocumentResponse';
import IntegrationsApi from './IntegrationsApi';
import FileApi from './FileApi';
import { data } from 'jquery';

const getAccessTokenHeaders = (wfAccessToken) => {
  if(!wfAccessToken) return {};

  return {
    headers: {
      'Workflow-Token': wfAccessToken?.hash,
    }
  };
};


const UWEEntityApi = {
  async getWorkTray({ resource, offset, size, sortBy, ...filters }) {
    const result = await checkErrors(await fetchApi.api.get(resource, { query: { offset, size, sortBy, ...filters } }));

    return result.data ? result.data : result;
  },

  async getOpenCases() {
    return checkErrors(await fetchApi.api.get('uwe-entities/work-cases/'));
  },

  async getUWEEntity({ entityId, wfAccessToken }) {
    const entity = await checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}`, getAccessTokenHeaders(wfAccessToken)));

    return entity;
  },

  async getUWEEntityStatus({ entityId, wfAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/status`, getAccessTokenHeaders(wfAccessToken)))).data;
  },

  async performActionOnUWEEntity({ id, action }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/perform-action/${action}`));
  },

  async getUWEEntityHistory({ id, wfAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${id}/history`, getAccessTokenHeaders(wfAccessToken)))).data;
  },

  async getWorkflowInfoForSlugForAnonymous({ slug, tenant } = {}) {
    return (await checkErrors(await fetchApi.api.get(`anon/${tenant}/workflows/${slug}/info`))).data;
  },

  async getWorkflowInfoForSlug({ slug } = {}) {
    return (await checkErrors(await fetchApi.api.get(`workflows/info/${slug}`))).data;
  },
  async getWorkflowInfoForId({ id } = {}) {
    return (await checkErrors(await fetchApi.api.get(`workflows/info/workflow:${id}`))).data;
  },

  async getWorkflowStates({ workflowId, workflowName } = {}) {
    const query = { id: `workflow:${workflowName || workflowId}` };

    return (await checkErrors(await fetchApi.lookup.get('workflow-states', { query }))).data;
  },

  async getWorkflowStatesForEntity({ entityId } = {}) {
    const query = { id: `entity:${entityId}` };

    return (await checkErrors(await fetchApi.lookup.get('workflow-states', { query }))).data;
  },

  async getWorkflowStatusMap({ id } = {}) {
    const states = (await UWEEntityApi.getWorkflowStatesForEntity({ entityId: id })) || [];

    return states.reduce((_, { name, title }) => {
      _[name] = title || name;

      return _;
    }, {});
  },

  async getUWEEntityLog({ id, wfAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/log`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getUWEEntityProgressSteps({ entityId, fromParent , wfAccessToken }) {
    const query = {
      fromParent: fromParent ? '1' : undefined,
    };

    return checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/progress-steps`, { ...getAccessTokenHeaders(wfAccessToken),query }));
  },

  async teleportUWEEntityToLogEntry({ id, stepIdx }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/log/teleport`, { data: { index: stepIdx } }));
  },

  async testDocumentExpression({ id, options }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/document-expression`, { data: options }));
  },

  async testPreviewDocument({ id, options }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/document-test`, { data: options }));
  },

  async testInternalNotificationMessage({ id, object }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/internal-notification`, { data: object }));
  },

  async previewMetadata({ id, metadataObjectMap }) {
    return (await checkErrors(await fetchApi.api.post(`uwe-entities/${id}/preview-metadata`, { data: metadataObjectMap }))).data;
  },

  async getUWEEntityAssignments({ id , wfAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/assignments`, getAccessTokenHeaders(wfAccessToken)));
  },

  async setUWEEntityAssignment({ id, roleId, userId }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${id}/assign`, {
        data: { roleId, userId },
      })
    );
  },

  async setEntityWorkflowStep({ id, step }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${id}/set-step`, {
        data: { step },
      })
    );
  },

  async createUWEEntity({ page, formData, wfAccessToken }) {
    return checkErrors(await fetchApi.api.post('uwe-entities/', {...getAccessTokenHeaders(wfAccessToken), data: { ...formData, page, workflowAccessTokenId: wfAccessToken.id } }));
  },

  async updateUWEEntity({ id, page, formData,wfAccessToken }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/${page}`, { ...getAccessTokenHeaders(wfAccessToken),data: formData }));
  },

  async createUWEEntityAsAnonymous({ payload: { page, formData }, slug, tenant }) {
    return checkErrors(await fetchApi.api.post(`anon/${tenant}/workflows/${slug}/uwe-entities/new`, { data: { ...formData, page } }));
  },

  async updateUWEEntityAsAnonymous({ payload: { id, page, formData }, slug, tenant }) {
    return checkErrors(await fetchApi.api.put(`anon/${tenant}/workflow/${slug}/uwe-entities/${id}/${page}`, { data: formData }));
  },

  async addProducts({ id, productIds }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/products`, { data: { productIds } }));
  },

  async awaitDocuments({ id, page, wfAccessToken }) {
    return checkErrors(
      await fetchApi.api.put(`uwe-entities/${id}/await-documents`, {
        ...getAccessTokenHeaders(wfAccessToken),
        data: {
          page,
        },
      })
    );
  },

  async createUWEEntityNote({ entityId, note, wfAccessToken }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${entityId}/notes`, {...getAccessTokenHeaders(wfAccessToken),
        data: note,
        json: true,
      })
    );
  },

  async getUWEEntityNotes({ entityId, wfAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/notes`));
  },

  async getRoleAssignments({ id }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/role-assignments`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getUWEEntityZipDownloadUrl({ id, filter, wfAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${id}/download-documents`, { ...getAccessTokenHeaders(wfAccessToken), query: { filter } }))).data;
  },
  async getWorkflowRoles({ id, wfAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/workflow-roles`, getAccessTokenHeaders(wfAccessToken)));
  },
  async getProgressByOrganization({ id }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/getProgressByOrganization/${id}`));
  },

  async generatePreviewFor({ id, data }) {
    return (
      await checkErrors(
        await fetchApi.api.post(`uwe-entities/${id || 0}/form-preview`, {
          data,
          json: true,
        })
      )
    ).data;
  },

  async verifyPreviewGeneration({ referenceId }) {
    const response = checkDocumentResponse((await checkErrors(await fetchApi.api.get(`uwe-entities/form-preview/verify/${referenceId}`))).data);

    if (response.status === STATUS.NOTFOUND || response.status === STATUS.ERROR) {
      throw new Error('Error buscando documento.');
    }

    return response;
  },

  async submissionFromEmail({ token }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/submissionFromEmail/${token}`))).data;
  },

  
  async createWfAccessTokenHash({  workflowId, uweEntityId, canView, canCreate, canUpdate }) {
    return checkErrors(
      await fetchApi.api.post('uwe-entities/workflow-access-token', {
        data: { workflowId, uweEntityId, canView, canCreate, canUpdate },
      })
    );
  },
};

export default UWEEntityApi;
